import React, { ReactElement } from 'react';
import styles from './ThreeDotMenu.module.scss';
import { IconButton, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { slugify } from 'new/utils/slugify';

export interface Action {
  title: string;
  icon?: ReactElement;
  onClick: () => void;
  color?: string;
}

export interface Section {
  section?: string;
  actions: Action[];
}

interface ThreeDotMenuProps {
  actions: Section[] | Action[];
}

export const ThreeDotMenu = (props: ThreeDotMenuProps) => {
  const { actions } = props;
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (onClick) => (e) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
    onClick();
  };

  if (!actions || !Array.isArray(actions) || actions.length === 0) {
    return null;
  }

  const sections = (!actions[0]?.actions ? [{ actions }] : actions) as Section[];

  return (
    <>
      <IconButton
        data-testid="threedot-menu-icon-button"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose(() => {})}
      >
        <div className={styles.menu}>
          {sections.map(({ section, actions }, index) => (
            <div key={index} className={styles.menuList}>
              {section ? (
                <Typography variant="overline" className={styles.section}>
                  {section}
                </Typography>
              ) : null}
              {actions.map(({ color, title, onClick, icon }) => (
                <MenuItem
                  key={title}
                  data-testid={`threedot-menu-action-${slugify(title)}`}
                  className={styles.item}
                  onClick={handleClose(onClick)}
                >
                  {!!icon
                    ? React.cloneElement(icon, {
                        style: {
                          fill: color ? theme.palette[color]?.main : undefined,
                        },
                      })
                    : null}
                  <Typography variant="body2" color={color}>
                    {title}
                  </Typography>
                </MenuItem>
              ))}
            </div>
          ))}
        </div>
      </Menu>
    </>
  );
};
